import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared fetch of the public General settings (banner + maintenance).
const usePublicSettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/settings/public`).then(({ data }) => { if (alive) setS(data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return s;
};

// Dismissible announcement banner, public marketing/platform pages only. Hidden
// while maintenance mode is on. Dismissal is per-session (sessionStorage), keyed on
// the banner text so a new announcement reappears.
export const AnnouncementBanner = () => {
  const { t } = useTranslation();
  const s = usePublicSettings();
  const ab = s?.announcement_banner;
  const maint = s?.maintenance_mode?.enabled;
  const key = ab?.text ? `samtwo_ann_dismissed_${btoa(unescape(encodeURIComponent(ab.text))).slice(0, 24)}` : "";
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (key) { try { setDismissed(sessionStorage.getItem(key) === "1"); } catch { /* noop */ } }
  }, [key]);
  if (!ab?.enabled || !ab.text || maint || dismissed) return null;
  const dismiss = () => { try { sessionStorage.setItem(key, "1"); } catch { /* noop */ } setDismissed(true); };
  return (
    <div data-testid="announcement-banner" className="w-full px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium relative z-40"
      style={{ background: "var(--accent-primary)", color: "#fff" }}>
      {ab.link_url
        ? <a href={ab.link_url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" data-testid="announcement-banner-link">{ab.text}</a>
        : <span>{ab.text}</span>}
      <button onClick={dismiss} data-testid="announcement-banner-dismiss" aria-label={t("common.dismiss")} className="absolute right-3 opacity-80 hover:opacity-100">
        <X size={16} />
      </button>
    </div>
  );
};

// Full-screen maintenance screen for public/non-admin users when maintenance mode
// is on. Admins and admin routes bypass (handled by `bypass`).
export const MaintenanceGate = ({ bypass, children }) => {
  const { t } = useTranslation();
  const s = usePublicSettings();
  if (bypass || !s || !s.maintenance_mode?.enabled) return children;
  return (
    <div data-testid="maintenance-screen" className="min-h-screen flex items-center justify-center px-6 text-center"
      style={{ background: "var(--bg-primary)", color: "var(--text-primary)" }}>
      <div className="max-w-lg">
        <Wrench size={40} className="mx-auto mb-5" style={{ color: "var(--accent-primary)" }} />
        <h1 className="text-3xl font-bold mb-3">{t("siteGates.maintenanceTitle")}</h1>
        <p className="text-base" style={{ color: "var(--text-secondary)" }}>{s.maintenance_mode.message}</p>
      </div>
    </div>
  );
};
